import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { message } from 'antd'
import { useEffect, useState } from 'react'
import { ProjectStatus, ProjectType } from '../../common/constants'
import { JobListItem } from '../JobList/jobListItem'
import { boardSortColumn, boardSortOptions } from '../JobList/jobListFiltersModel'
import { JobListFilterState } from '../JobList/useJobListFilters'
import JobKanbanCard from './JobKanbanCard'
import JobKanbanColumn from './JobKanbanColumn'
import { useSetEstimateLostMutation, useSetJobStatusMutation } from './jobKanbanApi'
import WorkareaPickerModal from './WorkareaPickerModal'
import { effectiveStatusOf, PendingMove, removePendingMove, resolveDrop, undoTargetFor } from './jobKanbanDragModel'
import {
  columnsFor, DRAG_PREVIEW_NOTICE, labelFor, needsWorkareaPickerFor, statusIsSavableFor
} from './jobKanbanModel'
import { EstimateStatuses } from '../../common/constants'
import './JobKanbanBoard.css'

type Props = {
  jobType?: ProjectType
  deleted?: boolean
  sold?: boolean
  filters: JobListFilterState
  condensed?: boolean
}

const DRAG_ACTIVATION_DISTANCE = 8

const JobKanbanBoard = ({ jobType, deleted, sold = true, filters, condensed }: Props) => {
  const columns = columnsFor(sold)
  const [pendingMoves, setPendingMoves] = useState<PendingMove[]>([])
  const [draggingJob, setDraggingJob] = useState<JobListItem | undefined>()

  /* The sort is shared with the list, which can order by columns the board has no
     use for. Ask the server for what the Sort control shows, so the two agree. */
  const request = {
    ...filters.request,
    sortColumn: boardSortColumn(filters.request.sortColumn, boardSortOptions({ sold, deleted: !!deleted }))
  }

  const statusIsSavable = statusIsSavableFor({ sold, deleted, jobType })
  const needsWorkareaPicker = needsWorkareaPickerFor({ sold, deleted, jobType })
  const [picking, setPicking] = useState<{ job: JobListItem, status: ProjectStatus } | undefined>()

  const dropPendingMove = (jobId: number) => setPendingMoves(moves => removePendingMove(moves, jobId))

  const onMutationFailure = (jobId: number, failure: string) => {
    dropPendingMove(jobId)
    message.error(failure)
  }

  const setJobStatus = useSetJobStatusMutation({ onFailure: onMutationFailure })
  const setEstimateLost = useSetEstimateLostMutation({ onFailure: onMutationFailure })

  /* An estimate's status is derived from its proposals, so the only thing a drop can write
     is the Lost flag: into Lost sets it, out of Lost clears it and lets the proposals decide
     which column the card lands in. */
  const estimateLostIsSavable = !sold && !deleted

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE }
  }))

  const requestKey = JSON.stringify(request)
  useEffect(() => {
    setPendingMoves([])
  }, [requestKey])

  const onDragStart = ({ active }: DragStartEvent) => {
    setDraggingJob(active.data.current?.job as JobListItem | undefined)
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggingJob(undefined)

    const resolution = resolveDrop(
      columns,
      sold,
      pendingMoves,
      { job: active.data.current?.job, from: active.data.current?.from },
      { status: over?.data.current?.status }
    )

    if (!resolution.moved) {
      if (resolution.refusedReason) {
        message.warning(`${resolution.job!.name} cannot move to ${labelFor(columns, resolution.to!)} — ${resolution.refusedReason.toLowerCase()}.`)
      }
      return
    }

    setPendingMoves(resolution.moves)
    const job = resolution.job!
    const destination = labelFor(columns, resolution.to!)

    if (resolution.undone) {
      message.info(`${job.name} put back in ${destination}.`)
      return
    }

    if (statusIsSavable) {
      setJobStatus.mutate({ jobId: job.id, status: resolution.to as ProjectStatus })
      return
    }

    if (needsWorkareaPicker) {
      setPicking({ job, status: resolution.to as ProjectStatus })
      return
    }

    if (estimateLostIsSavable) {
      setEstimateLost.mutate({ jobId: job.id, lost: resolution.to === EstimateStatuses.Lost })
      return
    }

    message.info(`${job.name} → ${destination}. ${DRAG_PREVIEW_NOTICE}`)
  }

  const cancelPicking = () => {
    if (picking) {
      dropPendingMove(picking.job.id)
      setPicking(undefined)
    }
  }

  const confirmPicking = (workareaIds: number[]) => {
    if (!picking) {
      return
    }
    setJobStatus.mutate({ jobId: picking.job.id, status: picking.status, workareaIds })
    setPicking(undefined)
  }

  const draggingAccent = draggingJob
    ? columns.find(column => column.status === effectiveStatusOf(draggingJob, sold, pendingMoves))?.accent
    : undefined

  const undoTargetStatus = draggingJob ? undoTargetFor(draggingJob, pendingMoves) : undefined

  return <DndContext
    sensors={sensors}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onDragCancel={() => setDraggingJob(undefined)}
  >
    <div className="job-kanban" role="list" aria-label="Pipeline columns">
      {columns.map(column => (
        <JobKanbanColumn
          key={column.status}
          column={column}
          jobType={jobType}
          sold={sold}
          request={request}
          pendingMoves={pendingMoves}
          dragging={!!draggingJob}
          isUndoTarget={undoTargetStatus === column.status}
          condensed={condensed}
          onServerCaughtUp={dropPendingMove}
        />
      ))}
    </div>

    <DragOverlay dropAnimation={null} zIndex={9999}>
      {draggingJob && <JobKanbanCard
        job={draggingJob}
        accent={draggingAccent ?? 'transparent'}
        overlay
        condensed={condensed}
      />}
    </DragOverlay>

    {picking && <WorkareaPickerModal
      job={picking.job}
      status={picking.status}
      columns={columns}
      saving={setJobStatus.isLoading}
      onCancel={cancelPicking}
      onConfirm={confirmPicking}
    />}
  </DndContext>
}

export default JobKanbanBoard
